/**
 * ProjectPage.tsx — one call, one page (§6.1).
 *
 * The landing page has to answer four questions at once: what is in this
 * project, is the Engine able to read it, what is being changed right now, and
 * what is wrong with it. `/status` answers all four in a single response, so
 * the page is not a waterfall of requests each of which can fail separately.
 *
 * The compatibility banner is not a warning. An Engine that cannot interpret
 * the project's contract must not be used to edit it, and the page says that
 * before offering a single diagram.
 */

import { useClient } from "../../api/ClientContext";
import { routeHref } from "../../app/routing";
import { ErrorBanner } from "../../components/ErrorBanner";
import { Empty, Panel } from "../../components/Panel";
import { StatusPill } from "../../components/StatusPill";
import { disciplineDisplay } from "../../disciplines/registry";
import { useAsync } from "../../stores/useAsync";
import { ValidationPanel } from "../validation/ValidationPanel";

export function ProjectPage({ projectId }: { projectId: string }): JSX.Element {
  const client = useClient();
  const status = useAsync(() => client.projectStatus(projectId), [client, projectId]);
  const project = status.data;

  return (
    <div className="page">
      <header className="page-head">
        <h1>{project?.title ?? projectId}</h1>
        <p className="muted">
          {project ? `canonical ${project.canonicalRevision.slice(0, 12)} · Engine ${project.engine.version}` : "loading…"}
        </p>
      </header>

      <ErrorBanner error={status.error} onDismiss={status.reload} />

      {project && !project.engine.compatible && (
        <div className="banner banner-error" role="alert">
          <div>
            <strong>This Engine cannot edit this project</strong>
            <p>{project.engine.incompatibility ?? "The project lock and the running Engine disagree."}</p>
            <p className="muted">
              Project lock {project.engine.projectLockVersion ?? "unknown"} · Engine {project.engine.version}
            </p>
          </div>
        </div>
      )}

      <div className="columns">
        <Panel title="Diagrams">
          {!project ? <Empty>Loading…</Empty>
            : project.diagrams.length === 0 ? <Empty>This project has no diagrams yet.</Empty>
            : (
              <ul className="diagram-list">
                {project.diagrams.map(diagram => {
                  const discipline = disciplineDisplay(diagram.diagramType);
                  return (
                    <li key={diagram.diagramId}>
                      <a href={routeHref({ name: "diagram", projectId, diagramId: diagram.diagramId })}>
                        <span className="badge" style={{ background: discipline.accent }}>{discipline.label}</span>
                        <strong>{diagram.title}</strong>
                        <span className="muted">{diagram.diagramId}</span>
                      </a>
                      <span className="muted">
                        {diagram.symbolCount} symbols · {diagram.traceCount} traces · rev {diagram.revision}
                        {diagram.retired ? " · retired" : ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
        </Panel>

        <div className="stack">
          <Panel
            title="Change sets"
            actions={
              <>
                <a className="ghost" href={routeHref({ name: "capabilities", projectId })}>Capabilities</a>
                <a className="ghost" href={routeHref({ name: "audit", projectId })}>Audit</a>
              </>
            }
          >
            {!project?.changeSets.length ? <Empty>Nothing in flight.</Empty> : (
              <ul className="changeset-list">
                {project.changeSets.map(changeSet => (
                  <li key={changeSet.changeSetId}>
                    <StatusPill status={changeSet.status} />
                    <a href={routeHref({ name: "changeSet", projectId, changeSetId: changeSet.changeSetId })}>
                      <strong>{changeSet.title}</strong>
                    </a>
                    <span className="muted">{changeSet.createdBy} · {changeSet.createdAt.slice(0, 16).replace("T", " ")}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Symbol dependencies">
            {!project?.symbolDependencies.length ? (
              <Empty>This project pins no registry symbols. Its definitions are project-local.</Empty>
            ) : (
              <table className="deps">
                <thead><tr><th>Alias</th><th>Symbol</th><th>Variant</th><th>Version</th></tr></thead>
                <tbody>
                  {project.symbolDependencies.map(dependency => (
                    <tr key={dependency.alias}>
                      <td><code>{dependency.alias}</code></td>
                      <td>{dependency.symbolId}</td>
                      <td>{dependency.variantId}</td>
                      <td><code>{dependency.version}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          <ValidationPanel summary={project?.validation} onRefresh={status.reload} busy={status.loading} />
        </div>
      </div>
    </div>
  );
}
